import React, { useState } from 'react';
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Col, Row } from "react-bootstrap";
import Langs from '../../app/lang/langs';
import { ViewSelectButton } from '../../components/buttons/ViewSelectButton';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import ManagersPermissionTab from './ManagersPermissionTab';
import ProfileManagementTab from './ProfileManagementTab';
import pStyles from './Permissions.module.css';
import MobileManagersPermissionTab from './mobile/MobileManagersPermissionTab';
import TeamsManagersTab from './TeamsManagersTab';
import CollabsManagersTab from './CollabsManagersTab';

export default function PermissionsView() {

    const navigate = useNavigate();
    const urlParams = useParams();

    const isMobile = useSelector((state) => state.settings.isMobile);
    const selectedTeamsIds = useSelector((state) => state.teams.selectedTeamsIds);

    const [viewsOptions] = useState([
        { label: <Langs str="PROFILES_MANAGEMENT" />, value: 0 },
        { label: <Langs str="TEAMS_MANAGEMENT" />, value: 1 },
    ]);

    const selectedView = urlParams.subView ? parseInt(urlParams.subView) : 0;
    const subTab = urlParams.label ? parseInt(urlParams.label) : 0;
    const [preselectedProfile, setPreselectedProfile] = useState(null);

    const setSubTab = (tab) => {
        let selectedTeamId = selectedTeamsIds[0];
        navigate(`/permissions/${selectedTeamId}/${selectedView}/${tab}`);
    };

    const handleViewPermissions = (profileName) => {
        setPreselectedProfile(profileName);
        setSubTab(1);
    };

    const handleViewChange = (view) => {
        if (view != null) {
            let selectedTeamId = selectedTeamsIds[0];
            navigate(`/permissions/${selectedTeamId}/${view}/0`);
        }
    };

    const currentLabel = viewsOptions.find((o) => o.value == selectedView)?.label;

    const isPhone = isMobile === 2;

    // A phone has no room for a view switcher above a tab strip, and the fixed
    // `.topMenu` is 65px tall with `overflow: hidden`, so the switcher that sits
    // in it is clipped out of reach there. The four destinations are flattened
    // into one scrollable strip instead — same routes, same URLs.
    const mobileTabs = [
        { view: 0, sub: 0, key: 'PROFILE_MANAGEMENT' },
        { view: 0, sub: 1, key: 'PROFILE_PERMISSIONS' },
        { view: 1, sub: 0, key: 'MANAGERS_BY_TEAM' },
        { view: 1, sub: 1, key: 'MANAGERS_BY_COLLAB' },
    ];

    const mobileTabIndex = Math.max(
        0,
        mobileTabs.findIndex((t) => t.view === selectedView && t.sub === subTab)
    );

    const handleMobileTabChange = (index) => {
        const target = mobileTabs[index];
        if (!target) return;
        const selectedTeamId = selectedTeamsIds[0];
        navigate(`/permissions/${selectedTeamId}/${target.view}/${target.sub}`);
    };

    function renderMobileBody() {
        if (selectedView === 1) {
            return subTab === 1 ? <CollabsManagersTab /> : <TeamsManagersTab />;
        }
        return subTab === 1
            ? <MobileManagersPermissionTab defaultProfile={preselectedProfile} />
            : <ProfileManagementTab onViewPermissions={handleViewPermissions} />;
    }

    function renderMobile() {
        return (
            <>
                <Box sx={{
                    borderBottom: '2px solid rgba(0, 40, 128, 0.06)',
                    background: 'rgba(255, 255, 255, 0.92)',
                    backdropFilter: 'blur(10px)',
                    mx: -1,
                }}>
                    <Tabs
                        value={mobileTabIndex}
                        onChange={(e, v) => handleMobileTabChange(v)}
                        variant="scrollable"
                        scrollButtons={false}
                        allowScrollButtonsMobile
                        sx={{
                            minHeight: 52,
                            '& .MuiTabs-indicator': {
                                height: 3,
                                borderRadius: '3px 3px 0 0',
                                background: 'linear-gradient(90deg, #FF8701, #FFC100)',
                            },
                        }}
                    >
                        {mobileTabs.map((t) => (
                            <Tab
                                key={`${t.view}-${t.sub}`}
                                label={<Langs str={t.key} />}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: '0.9rem',
                                    color: 'rgba(0, 40, 128, 0.6)',
                                    minHeight: 52,
                                    px: 2,
                                    '&.Mui-selected': { color: '#FF9015', fontWeight: 600 },
                                }}
                            />
                        ))}
                    </Tabs>
                </Box>
                <Box sx={{ pt: 0.5 }}>
                    {renderMobileBody()}
                </Box>
            </>
        );
    }

    function renderProfilesManagement() {
        return (
            <>
                <Box sx={{
                    borderBottom: '2px solid rgba(0, 40, 128, 0.06)',
                    px: 2,
                    background: 'rgba(255, 255, 255, 0.85)',
                    backdropFilter: 'blur(10px)',
                }}>
                    <Tabs
                        value={subTab}
                        onChange={(e, v) => setSubTab(v)}
                        sx={{
                            minHeight: 48,
                            '& .MuiTabs-indicator': {
                                height: 3,
                                borderRadius: '3px 3px 0 0',
                                background: 'linear-gradient(90deg, #FF8701, #FFC100)',
                                transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                            },
                            '& .MuiTabs-flexContainer': {
                                gap: '4px',
                            },
                        }}
                    >
                        <Tab
                            label={<Langs str="PROFILE_MANAGEMENT" />}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                letterSpacing: '-0.01em',
                                color: 'rgba(0, 40, 128, 0.55)',
                                minHeight: 48,
                                px: 2.5,
                                py: 1.5,
                                borderRadius: '10px 10px 0 0',
                                transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    color: 'rgba(0, 40, 128, 0.85)',
                                    backgroundColor: 'rgba(255, 144, 21, 0.04)',
                                },
                                '&.Mui-selected': {
                                    color: '#FF9015',
                                    fontWeight: 600,
                                },
                            }}
                        />
                        <Tab
                            label={<Langs str="PROFILE_PERMISSIONS" />}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                letterSpacing: '-0.01em',
                                color: 'rgba(0, 40, 128, 0.55)',
                                minHeight: 48,
                                px: 2.5,
                                py: 1.5,
                                borderRadius: '10px 10px 0 0',
                                transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    color: 'rgba(0, 40, 128, 0.85)',
                                    backgroundColor: 'rgba(255, 144, 21, 0.04)',
                                },
                                '&.Mui-selected': {
                                    color: '#FF9015',
                                    fontWeight: 600,
                                },
                            }}
                        />
                    </Tabs>
                </Box>
                {subTab === 0 && <ProfileManagementTab onViewPermissions={handleViewPermissions} />}
                {/* The phone path returns earlier, so this branch is always the desktop one. */}
                {subTab === 1 && <ManagersPermissionTab defaultProfile={preselectedProfile} />}
            </>
        );
    }

    function renderTeamsManagement() {
        return (
            <>
                <Box sx={{
                    borderBottom: '2px solid rgba(0, 40, 128, 0.06)',
                    px: 2,
                    background: 'rgba(255, 255, 255, 0.85)',
                    backdropFilter: 'blur(10px)',
                }}>
                    <Tabs
                        value={subTab}
                        onChange={(e, v) => setSubTab(v)}
                        sx={{
                            minHeight: 48,
                            '& .MuiTabs-indicator': {
                                height: 3,
                                borderRadius: '3px 3px 0 0',
                                background: 'linear-gradient(90deg, #FF8701, #FFC100)',
                                transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                            },
                            '& .MuiTabs-flexContainer': {
                                gap: '4px',
                            },
                        }}
                    >
                        <Tab
                            label={<Langs str="TEAMS_MANAGERS" />}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                letterSpacing: '-0.01em',
                                color: 'rgba(0, 40, 128, 0.55)',
                                minHeight: 48,
                                px: 2.5,
                                py: 1.5,
                                borderRadius: '10px 10px 0 0',
                                transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    color: 'rgba(0, 40, 128, 0.85)',
                                    backgroundColor: 'rgba(255, 144, 21, 0.04)',
                                },
                                '&.Mui-selected': {
                                    color: '#FF9015',
                                    fontWeight: 600,
                                },
                            }}
                        />
                        <Tab
                            label={<Langs str="COLLABS_MANAGERS_PROFILES" />}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                letterSpacing: '-0.01em',
                                color: 'rgba(0, 40, 128, 0.55)',
                                minHeight: 48,
                                px: 2.5,
                                py: 1.5,
                                borderRadius: '10px 10px 0 0',
                                transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    color: 'rgba(0, 40, 128, 0.85)',
                                    backgroundColor: 'rgba(255, 144, 21, 0.04)',
                                },
                                '&.Mui-selected': {
                                    color: '#FF9015',
                                    fontWeight: 600,
                                },
                            }}
                        />
                    </Tabs>
                </Box>
                {subTab === 0 && <TeamsManagersTab />}
                {subTab === 1 && <CollabsManagersTab />}
            </>
        );
    }

    function renderBody() {
        switch (selectedView) {
            case 0:
                return renderProfilesManagement();
            case 1:
                return renderTeamsManagement();
            default:
                return null;
        }
    }

    if (isPhone) {
        return (
            <div className={pStyles.permissionsWrapper}>
                <div className="topMenu">
                    <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <span className="headerTitle" style={{ fontSize: 20 }}>
                            {currentLabel}
                        </span>
                    </div>
                </div>
                <div className={pStyles.permissionsBody}>
                    {renderMobile()}
                </div>
            </div>
        );
    }

    return (
        <div className={pStyles.permissionsWrapper}>
            <div className="topMenu">
                <Row style={{ alignItems: "center" }}>
                    <Col md={3}>
                        <span className="headerTitle">
                            {currentLabel}
                        </span>
                    </Col>
                    <Col md={6} className="d-flex justify-content-center pe-4 align-items-center p-0 m-0">
                        <ViewSelectButton
                            options={viewsOptions}
                            selectedView={selectedView}
                            onViewChange={handleViewChange}
                        />
                    </Col>
                    <Col md={3} className="d-flex justify-content-end pe-4 align-items-center p-0 m-0 ms-auto">
                    </Col>
                </Row>
            </div>
            <div className={pStyles.permissionsBody}>
                {renderBody()}
            </div>
        </div>
    );
}
